import React, { useState, useEffect } from 'react';
import { Card, Table, Tag, Spin, Row, Col } from 'antd';
import { TrophyOutlined } from '@ant-design/icons';
import { useHistory } from 'react-router-dom';
import ApiClient from '../../../methods/api/apiClient';

const COLORS = {
  primary: '#3E549D',
  success: '#27ae60',
  warning: '#f39c12',
  danger: '#e74c3c',
};

const PerformanceMetrics = ({ board_id, dateRange, summaryData }) => {
  const [loading, setLoading] = useState(true);
  const [carriers, setCarriers] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [delayData, setDelayData] = useState(null);
  const history = useHistory();

  useEffect(() => {
    // Use pre-fetched summary data if available
    if (summaryData) {
      if (summaryData.carrierPerformance) {
        const d = Array.isArray(summaryData.carrierPerformance) ? summaryData.carrierPerformance : [];
        setCarriers(d.slice(0, 5));
      }
      if (summaryData.driverPerformance) {
        const d = Array.isArray(summaryData.driverPerformance) ? summaryData.driverPerformance : [];
        setDrivers(d.slice(0, 5));
      }
      if (summaryData.carrierDelayInDelivered) {
        setDelayData(summaryData.carrierDelayInDelivered);
      }
      setLoading(false);
      return;
    }

    // Fallback: fetch individually if summaryData not provided
    const buildParams = () => {
      const params = {};
      if (board_id) params.board_id = board_id;
      if (dateRange?.startDate) params.startDate = dateRange.startDate;
      if (dateRange?.endDate) params.endDate = dateRange.endDate;
      return params;
    };

    const fetchPerformance = () => {
      setLoading(true);
      const params = buildParams();

      const calls = [
        ApiClient.get('performance/carrier', params, '', true)
          .then(res => {
            if (res?.success || res?.code === 200) {
              const d = Array.isArray(res?.data) ? res.data : (res?.data?.data || []);
              return d.slice(0, 5);
            }
            return [];
          })
          .catch(() => []),
        ApiClient.get('performance/driver', params, '', true)
          .then(res => {
            if (res?.success || res?.code === 200) {
              const d = Array.isArray(res?.data) ? res.data : (res?.data?.data || []);
              return d.slice(0, 5);
            }
            return [];
          })
          .catch(() => []),
        ApiClient.get('performance/carrier/delay-in-delivered', params, '', true)
          .then(res => (res?.success || res?.code === 200) ? res?.data : null)
          .catch(() => null),
      ];

      Promise.all(calls)
        .then(([carrierData, driverData, delay]) => {
          setCarriers(carrierData);
          setDrivers(driverData);
          setDelayData(delay);
        })
        .finally(() => setLoading(false));
    };

    fetchPerformance();
  }, [board_id, dateRange, summaryData]);

  const getDelayTag = (hours) => {
    if (hours === undefined || hours === null) return <Tag color="default">N/A</Tag>;
    const h = Number(hours);
    if (h <= 1) return <Tag color="green">On Time</Tag>;
    if (h <= 4) return <Tag color="orange">Minor Delay</Tag>;
    return <Tag color="red">Delayed</Tag>;
  };

  const nameRender = (field) => (text, record) => (
    <span style={{ fontWeight: 500, fontSize: 13 }}>
      {text || record[field] || record.fullName || 'Unknown'}
    </span>
  );
  const loadsRender = (val, record) => (
    <span style={{ fontWeight: 600, color: COLORS.primary }}>
      {val || record.totalLoads || record.count || 0}
    </span>
  );

  const carrierCols = [
    { title: 'Carrier', dataIndex: 'name', key: 'name', ellipsis: true, render: nameRender('carrier_name') },
    { title: 'Loads', dataIndex: 'total_loads', key: 'total_loads', width: 60, align: 'center', render: loadsRender },
    { title: 'Status', dataIndex: 'avg_delay', key: 'avg_delay', width: 100, align: 'center',
      render: (val, record) => getDelayTag(val || record.avgDelay || record.delay_hours) },
  ];

  const driverCols = [
    { title: 'Driver', dataIndex: 'name', key: 'name', ellipsis: true, render: nameRender('driver_name') },
    { title: 'Loads', dataIndex: 'total_loads', key: 'total_loads', width: 60, align: 'center', render: loadsRender },
  ];

  const avgDelay = delayData
    ? typeof delayData === 'object'
      ? delayData.average || delayData.avg_delay || delayData.value || 'N/A'
      : delayData
    : 'N/A';

  return (
    <Card
      title={
        <span style={{ fontWeight: 600, fontSize: 15 }}>
          <TrophyOutlined style={{ color: COLORS.warning, marginRight: 8 }} />
          Performance
        </span>
      }
      extra={
        <button
          onClick={() => history.push('/performance-carriers1')}
          style={{
            background: 'none',
            border: '1px solid ' + COLORS.primary,
            color: COLORS.primary,
            borderRadius: 4,
            padding: '3px 12px',
            fontSize: 11,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          View All &rarr;
        </button>
      }
      bordered={false}
      style={{ borderRadius: 8, boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}
      styles={{ body: { padding: '12px 16px' } }}
    >
      <Spin spinning={loading}>
        {/* Average Delay Summary */}
        <div style={{
          background: '#f9fafb',
          borderRadius: 6,
          padding: '8px 12px',
          marginBottom: 12,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <span style={{ fontSize: 12, color: '#666' }}>Avg Delivery Delay</span>
          <span style={{ fontSize: 16, fontWeight: 700, color: COLORS.danger }}>
            {avgDelay !== 'N/A' ? `${Number(avgDelay).toFixed(1)} hrs` : 'N/A'}
          </span>
        </div>

        <Row gutter={16}>
          <Col xs={24} md={12}>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#555', marginBottom: 6 }}>
              Top Carriers
            </div>
            <Table
              columns={carrierCols}
              dataSource={carriers.map((c, i) => ({ ...c, key: i }))}
              pagination={false}
              size="small"
              locale={{ emptyText: 'No carrier data' }}
              style={{ fontSize: 12 }}
            />
          </Col>
          <Col xs={24} md={12}>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#555', marginBottom: 6 }}>
              Top Drivers
            </div>
            <Table
              columns={driverCols}
              dataSource={drivers.map((d, i) => ({ ...d, key: i }))}
              pagination={false}
              size="small"
              locale={{ emptyText: 'No driver data' }}
              style={{ fontSize: 12 }}
            />
          </Col>
        </Row>
      </Spin>
    </Card>
  );
};

export default PerformanceMetrics;
